import React from 'react'
import { cn } from '../../../lib/utils'
import MeetingInfo from './MeetingInfo'
import { useBenefits } from '../BenefitsProvider'
import { useTranslation } from '~/hooks/useTranslation'
import { LinkButton } from '~/lib/ui/button'
import 'moment-business-days'
import { icons } from '../ServiceRequestIcons'
import SelectStatusIndicator from './SelectStatusIndicator'
import YnsIcon from '~/components/icons/YnsIcon'
import AtomButton from '~/components/_atoms/Button/index'
import { useFormatTime } from '~/hooks/useFormatTime'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../lib/ui/accordion'
import FeedbackCarousel from './FeedbackCarousel'

const CompletedIcon = ({ className }) => (
  <YnsIcon name="completed" className={cn(className)} />
)

const ClockIcon = () => <YnsIcon name="clock" />

const CalendarIcon = () => <YnsIcon name="calendar" />

const TrendingUp = () => <YnsIcon name="trendingup" />

export default function SelectServiceCard({
  requestType,
  title,
  description,
  buttonText,
  iconType,
  fullWidth,
  photoUrl,
  coachCard,
  classes,
  liaison,
  buttonUrl,
  actionRenderer,
  buttonChild,
  buttonChildShow,
  serviceGroup,
  ...props
}) {
  const { t } = useTranslation('service_card')
  const { serviceRequestList, resumeReview } = useBenefits()
  const serviceRequest = serviceRequestList[requestType] || {}
  const {
    calendly_event: calendlyEvent,
    completed: requestComplete,
    created_at: requestCreatedAt,
    due_at: requestDueAt,
    status: requestStatus,
    coach,
    meeting_via_calendly: meetingViaCalendly,
    cancelled_by_coach: cancelledByCoach,
    clifton_strengths_code: strengthsCode,
    completed_reoccurring_requests: completedReoccurringRequests = [],
    coach_documents: coachDocuments = [],
    coach_checklists: coachChecklists = [],
    coach_revision_remote_content: coachRevisionRemoteContent = {},
    is_cancelled_by_coach_change: isCancelledByCoachChange,
  } = serviceRequest
  const coachName = coach?.first_name

  const hasCoachChecklists = coachChecklists && coachChecklists.length > 0
  const hasCompletedReoccurringRequests =
    completedReoccurringRequests.length > 0
  const noShow = calendlyEvent?.service_request_no_show_id
  const isRequestPending =
    requestStatus === 'assigned' || requestStatus === 'revision_requested'
  const coachChange =
    calendlyEvent?.cancelled_by_coach_change || isCancelledByCoachChange
  const returnedStatus =
    resumeReview?.status === 'returned' &&
    serviceRequest.type === 'resume_review'
  const declinedStatus =
    resumeReview?.status === 'revision_declined' &&
    serviceRequest.type === 'resume_review'
  const scheduledStatus =
    requestStatus === 'scheduled' ||
    requestStatus === 'revision_meeting_requested'
  const callScheduled = calendlyEvent?.start_time && scheduledStatus
  const meetingScheduled = calendlyEvent && !noShow && scheduledStatus
  const meetingIsCancelled = requestStatus === 'cancelled'
  const needsScheduling =
    meetingViaCalendly && !calendlyEvent?.start_time && !meetingIsCancelled
  const attachments = (coachDocuments || [])
    .concat(coachChecklists || [])
    .concat(coachRevisionRemoteContent ? [coachRevisionRemoteContent] : [])

  let bgColor = 'bg-select-blue'
  let hoverBgColor =
    'hover:bg-intoo-blue-medium hover:text-white focus:text-white active:text-white'
  if (serviceGroup === 'strategic') {
    bgColor = 'bg-intoo-green-dark'
    hoverBgColor =
      'hover:bg-intoo-green-dark-800 hover:text-white focus:text-white active:text-white'
  } else if (serviceGroup === 'specialized') {
    bgColor = 'bg-intoo-green-medium'
    hoverBgColor =
      'hover:bg-intoo-green-medium-600 hover:text-white focus:text-white active:text-white'
  }

  const buttonStyles = cn(bgColor, hoverBgColor, 'w-full')
  const hasFeedbackCarousel = requestComplete && !serviceRequest.feedbacked
  const shouldShowLegacyButtonChild = buttonChild && buttonChildShow

  const renderPrimaryAction = () => {
    if (actionRenderer) {
      return actionRenderer({
        buttonUrl,
        buttonText,
        className: buttonStyles,
        disabled: !buttonUrl,
      })
    }

    if (shouldShowLegacyButtonChild) {
      return React.createElement(buttonChild, {
        buttonUrl,
        buttonText,
        className: buttonStyles,
        disabled: !buttonUrl,
      })
    }

    return (
      <LinkButton
        href={buttonUrl}
        className={buttonStyles}
        disabled={!buttonUrl}
      >
        {noShow ? t('reschedule_meeting') : buttonText}
      </LinkButton>
    )
  }

  return (
    <Wrapper
      eventName="Meeting"
      callScheduled={callScheduled}
      className={cn(
        'flex flex-col rounded-2xl border border-gray-200 p-5',
        hasFeedbackCarousel && 'h-full',
        classes,
        fullWidth && 'col-span-1 md:col-span-2',
      )}
      requestComplete={requestComplete}
      {...props}
    >
      <div
        className={cn(
          'flex flex-col items-center justify-between gap-6 md:flex-row',
          hasFeedbackCarousel && 'h-full md:items-stretch',
        )}
      >
        <div
          className={cn(
            'service-card flex-1',
            hasFeedbackCarousel && 'flex h-full flex-col',
          )}
        >
          <IconSection
            requestComplete={requestComplete}
            icons={icons}
            iconType={iconType}
            bgColor={bgColor}
          />
          <div className="mt-2 font-medium">{title}</div>

          {(!meetingIsCancelled || serviceRequest.completed) && (
            <p className={cn('mt-2', hasFeedbackCarousel && 'flex-grow')}>
              {description}
            </p>
          )}
          <div
            className={cn(
              'mt-3',
              hasFeedbackCarousel && 'flex flex-1 flex-col',
            )}
          >
            {meetingScheduled && !requestComplete && !returnedStatus ? (
              <MeetingInfo
                calendlyEvent={calendlyEvent}
                requestType={requestType}
                buttonText={buttonText}
                requestComplete={requestComplete}
                hasCompletedReoccurringRequests={
                  hasCompletedReoccurringRequests
                }
                eventName="Meeting"
                cardTitle={title}
                meetingViaCalendly={meetingViaCalendly}
                liaison={liaison}
                buttonUrl={buttonUrl}
                isSelect={true}
                buttonStyles={buttonStyles}
                serviceRequestsPageCard={true}
                {...props}
              />
            ) : isRequestPending ||
              (meetingIsCancelled && !serviceRequest.completed) ? (
              <MeetingCancelled
                meetingViaCalendly={meetingViaCalendly}
                cancelledByCoach={cancelledByCoach}
                coachName={coachName}
                buttonUrl={buttonUrl}
                hasCompletedReoccurringRequests={
                  hasCompletedReoccurringRequests
                }
                requestCreatedAt={requestCreatedAt}
                requestDueAt={requestDueAt}
                strengthsCode={strengthsCode}
                requestType={requestType}
                buttonStyles={buttonStyles}
                CalendarIcon={CalendarIcon}
                description={description}
                coachChange={coachChange}
                meetingIsCancelled={meetingIsCancelled}
                needsScheduling={needsScheduling}
              />
            ) : requestComplete ? (
              <div
                className={cn(
                  'flex flex-col gap-4',
                  hasFeedbackCarousel && 'h-full',
                )}
              >
                <CompletedCard serviceRequest={serviceRequest} />
                {hasCompletedReoccurringRequests && (
                  <ViewPreviousRequests
                    buttonUrl={buttonUrl}
                    hasCompletedReoccurringRequests={
                      hasCompletedReoccurringRequests
                    }
                    className={
                      hasCompletedReoccurringRequests ? buttonStyles : ''
                    }
                  />
                )}
                <AtomButton
                  variant={
                    hasCompletedReoccurringRequests ? 'solid' : 'outline'
                  }
                  className={
                    hasCompletedReoccurringRequests ? buttonStyles : ''
                  }
                  disabled={!buttonUrl}
                  href={buttonUrl}
                >
                  {hasCompletedReoccurringRequests ? (
                    buttonText
                  ) : (
                    <span className="flex items-center gap-2">
                      {t('view_feedback')}
                      <YnsIcon name="star" className="ml-2" />
                    </span>
                  )}
                </AtomButton>
                {!serviceRequest.feedbacked && (
                  <div className="mt-auto">
                    <FeedbackCarousel
                      serviceRequestId={serviceRequest.id}
                      serviceRequestName={serviceRequest.name}
                      serviceRequestType={serviceRequest.type}
                    />
                  </div>
                )}
              </div>
            ) : returnedStatus || declinedStatus ? (
              <div className="flex flex-col gap-4">
                <SelectStatusIndicator Icon={TrendingUp} color="blue" filled>
                  {t('in_progress')}
                </SelectStatusIndicator>
                <SelectStatusIndicator color={'green'} filled fullWidth>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="attachments" className="border-b-0">
                      <AccordionTrigger className="py-0 hover:no-underline">
                        {t('attachments')}{' '}
                        {attachments.length > 1
                          ? t('documents.plural', { count: attachments.length })
                          : t('documents.single', {
                              count: attachments.length,
                            })}
                      </AccordionTrigger>
                      <AccordionContent className="pb-0 pt-2">
                        <div className="flex flex-col gap-2">
                          {coachRevisionRemoteContent && (
                            <div className="flex items-center">
                              <a
                                href={coachRevisionRemoteContent.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-link"
                              >
                                {coachRevisionRemoteContent.name}
                              </a>
                            </div>
                          )}
                          {coachDocuments.map((doc, index) => (
                            <div
                              key={`doc-${index}`}
                              className="flex items-center"
                            >
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-link"
                              >
                                {doc.fileName}
                              </a>
                            </div>
                          ))}
                          {hasCoachChecklists &&
                            coachChecklists.map((checklist, index) => (
                              <div
                                key={`checklist-${index}`}
                                className="flex items-center"
                              >
                                <a
                                  href={checklist.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-link"
                                >
                                  {checklist.fileName}
                                </a>
                              </div>
                            ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </SelectStatusIndicator>
                <AtomButton
                  disabled={!buttonUrl}
                  variant="outline"
                  href={buttonUrl}
                >
                  <span className="flex items-center gap-2">
                    {t('view_feedback')}
                    <YnsIcon name="star" />
                  </span>
                </AtomButton>
                {hasCompletedReoccurringRequests && (
                  <ViewPreviousRequests
                    buttonUrl={buttonUrl}
                    hasCompletedReoccurringRequests={
                      hasCompletedReoccurringRequests
                    }
                  />
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <SelectStatusIndicator Icon={ClockIcon} filled>
                  {noShow ? t('no_show') : t('not_started')}
                </SelectStatusIndicator>
                {noShow ? (
                  <LinkButton
                    href={buttonUrl}
                    className={buttonStyles}
                    disabled={!buttonUrl}
                  >
                    {t('reschedule_meeting')}
                  </LinkButton>
                ) : (
                  renderPrimaryAction()
                )}
                {hasCompletedReoccurringRequests && (
                  <ViewPreviousRequests
                    buttonUrl={buttonUrl}
                    hasCompletedReoccurringRequests={
                      hasCompletedReoccurringRequests
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

const MeetingCancelled = ({
  meetingViaCalendly,
  cancelledByCoach,
  coachName,
  buttonUrl,
  hasCompletedReoccurringRequests,
  requestCreatedAt,
  requestDueAt,
  requestType,
  buttonStyles,
  CalendarIcon,
  description,
  coachChange,
  meetingIsCancelled,
  needsScheduling,
}) => {
  const { t } = useTranslation('service_card')
  const formatTime = useFormatTime()

  return meetingViaCalendly && meetingIsCancelled ? (
    <div>
      {coachChange ? (
        <p>{t('coach_change_description', { coachName })}</p>
      ) : cancelledByCoach ? (
        <p>{t('meeting_cancelled_description', { coachName })}</p>
      ) : (
        <p>{description}</p>
      )}
      <div className="flex flex-col gap-4">
        <SelectStatusIndicator Icon={CalendarIcon} color="blue" filled>
          {cancelledByCoach ? t('reschedule') : t('not_started')}
        </SelectStatusIndicator>
        <LinkButton
          href={buttonUrl}
          className={buttonStyles}
          disabled={!buttonUrl}
        >
          {t('reschedule_action')}
        </LinkButton>
        {hasCompletedReoccurringRequests && (
          <ViewPreviousRequests
            buttonUrl={buttonUrl}
            hasCompletedReoccurringRequests={hasCompletedReoccurringRequests}
          />
        )}
      </div>
    </div>
  ) : (
    <div>
      <p>
        {requestType === 'ServiceRequest::StrengthsAssessment'
          ? t('strengths_code_request_submitted')
          : needsScheduling
            ? t('needs_scheduling')
            : t('request_submitted', {
                createdAt: formatTime(requestCreatedAt, 'short'),
                updateBy: formatTime(requestDueAt, 'short'),
              })}
      </p>
      <div className="flex flex-col gap-4">
        <SelectStatusIndicator Icon={TrendingUp} color="blue" filled>
          {t('in_progress')}
        </SelectStatusIndicator>
        <LinkButton
          href={buttonUrl}
          className={buttonStyles}
          disabled={!buttonUrl}
        >
          {t('view_details')}
        </LinkButton>
        {hasCompletedReoccurringRequests && (
          <ViewPreviousRequests
            buttonUrl={buttonUrl}
            hasCompletedReoccurringRequests={hasCompletedReoccurringRequests}
          />
        )}
      </div>
    </div>
  )
}

const IconSection = ({ icons, iconType, bgColor }) => {
  const IconComponent = icons[iconType]
  return (
    IconComponent && (
      <div className={cn('w-fit rounded-[14px] p-3', bgColor)}>
        <IconComponent
          strokecolor={'currentColor'}
          className={cn('h-5 w-5 !text-white')}
        />
      </div>
    )
  )
}

const CompletedCard = ({ serviceRequest }) => {
  const { t } = useTranslation('service_card')
  const {
    coach_documents: coachDocuments,
    coach_checklists: coachChecklists,
    coach_revision_remote_content: coachRevisionRemoteContent,
  } = serviceRequest || {}
  const hasCoachDocuments = coachDocuments && coachDocuments.length > 0
  const hasCoachChecklists = coachChecklists && coachChecklists.length > 0
  const attachments = (coachDocuments || [])
    .concat(coachChecklists || [])
    .concat(coachRevisionRemoteContent ? [coachRevisionRemoteContent] : [])

  return (
    <div className="flex flex-wrap items-start gap-4">
      <SelectStatusIndicator Icon={CompletedIcon} color={'green'} filled>
        {t('completed')}
      </SelectStatusIndicator>
      {(hasCoachDocuments || coachRevisionRemoteContent) && (
        <div className="flex min-w-[240px] flex-1 flex-col gap-3">
          <SelectStatusIndicator color={'green'} filled fullWidth>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="attachments" className="border-b-0">
                <AccordionTrigger className="py-0 hover:no-underline">
                  {t('attachments')}{' '}
                  {attachments.length > 1
                    ? t('documents.plural', { count: attachments.length })
                    : t('documents.single', { count: attachments.length })}
                </AccordionTrigger>
                <AccordionContent className="pb-0 pt-2">
                  <div className="flex flex-col gap-2">
                    {coachRevisionRemoteContent && (
                      <div className="flex items-center">
                        <a
                          href={coachRevisionRemoteContent.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-link"
                        >
                          {coachRevisionRemoteContent.name}
                        </a>
                      </div>
                    )}
                    {coachDocuments.map((doc, index) => (
                      <div key={`doc-${index}`} className="flex items-center">
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-link"
                        >
                          {doc.fileName}
                        </a>
                      </div>
                    ))}
                    {hasCoachChecklists &&
                      coachChecklists.map((checklist, index) => (
                        <div
                          key={`checklist-${index}`}
                          className="flex items-center"
                        >
                          <a
                            href={checklist.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-link"
                          >
                            {checklist.fileName}
                          </a>
                        </div>
                      ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </SelectStatusIndicator>
        </div>
      )}
    </div>
  )
}

const Wrapper = ({ className, children }) => {
  return <div className={className}>{children}</div>
}

export const ViewPreviousRequests = ({
  buttonUrl,
  hasCompletedReoccurringRequests,
}) => {
  const { t } = useTranslation('service_card')
  return (
    hasCompletedReoccurringRequests && (
      <AtomButton
        variant="outline"
        href={
          buttonUrl
            ? buttonUrl +
              (buttonUrl.includes('?') ? '&' : '?') +
              'view_previous=true'
            : ''
        }
      >
        <span className="flex items-center gap-2">
          {t('view_feedback')}
          <YnsIcon name="star" />
        </span>
      </AtomButton>
    )
  )
}
